/**
 * The plan's allowance for skills and behavior rules (backend: agent_studio/plan_limits.py). As on the old path:
 * only adding is gated, the platform's skills (Lead Generation, the Wix onboarding's) never count. The panel shows
 * "2 / 3"; at a full allowance adding opens the upgrade dialog instead – also when Mo or the server refuses
 * (PLAN_LIMIT_EVENT, the builder's `limit_reached` event, a 409 with code plan_limit).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectedBot } from '../../contexts/SelectedBotContext.js';
import useUpgrade from '../../hooks/useUpgrade.js';
import StudioUpgradeModal from '../studioUpgradeModal';
import { PLAN_LIMIT_EVENT, studioApi } from './studioApi';
import { load, peek } from './studioCache';

const API = process.env.REACT_APP_API_BASE_URL;
export { PLAN_LIMIT_EVENT };
export const planLimitsKey = (botId) => `planlimits:${botId}`;

/** { plan, skills, behavior } – a limit of null means no limit; undefined while loading, null when it failed */
export function usePlanLimits(botId) {
  const [limits, setLimits] = useState(() => peek(planLimitsKey(botId)));
  useEffect(() => {
    let alive = true;
    load(planLimitsKey(botId), () => studioApi.get(botId, '/plan-limits'))
      .then((l) => alive && setLimits(l)).catch(() => alive && setLimits(null));
    return () => { alive = false; };
  }, [botId]);
  return limits;
}

/** what the spec says is used against the allowance (the backend counts; this is the fallback) */
export function planUsage(spec) {
  return spec.usage || {
    skills: spec.skills.filter((s) => s.counts_toward_plan !== false).length,
    behavior: spec.behavior.length,
  };
}

export const atLimit = (used, limit) => limit != null && used >= limit;

/** a server refusal (studioApi error) of the plan's allowance: { artifact, limit } or null */
export function planLimitOf(error) {
  const d = error?.detail;
  return d && d.code === 'plan_limit' ? { artifact: d.artifact, limit: d.limit } : null;
}

/** "2 / 3 skills" with a slim bar; full, it is a button that opens the upgrade dialog. While the allowance loads
 *  it keeps its place (no shift when it appears). */
export function UsageMeter({ used, limit, label, onUpgrade }) {
  const { t } = useTranslation();
  if (limit === undefined) {
    return <span className="as-usage is-pending" aria-hidden="true"><span className="as-usage-track" /><span className="as-usage-label">{label}</span></span>;
  }
  if (limit === null) return null;
  const full = used >= limit;
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 100;
  const body = (
    <>
      <span className="as-usage-track" aria-hidden="true"><span className="as-usage-fill" style={{ width: `${pct}%` }} /></span>
      <span className="as-usage-label">{label}</span>
    </>
  );
  return full ? (
    <button type="button" className="as-usage is-full" onClick={onUpgrade} title={t('upgrade_now')}>{body}</button>
  ) : (
    <span className="as-usage" role="status">{body}</span>
  );
}

/** the upgrade dialog for one kind; mounted once it was needed, so the platform's own upgrade flow can follow */
export function PlanUpgradeDialog({ open, artifact, used, limit, onClose }) {
  const { selectedBot } = useSelectedBot();
  const { handleUpgradeClick, UpgradeModal, isUpgradeLoading } = useUpgrade();
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    if (!open || plans.length) return undefined;
    let alive = true;
    fetch(`${API}subscription_plans`).then((r) => (r.ok ? r.json() : [])).then((d) => alive && setPlans(Array.isArray(d) ? d : []))
      .catch(() => {});
    return () => { alive = false; };
  }, [open, plans.length]);
  return (
    <>
      <StudioUpgradeModal open={open} onClose={onClose} bot={selectedBot} plans={plans}
        artifact={artifact === 'behavior' ? 'behaviour' : 'skill'} usedCount={used} limit={limit}
        onUpgrade={() => { onClose(); handleUpgradeClick(selectedBot); }}
        loading={!!selectedBot && isUpgradeLoading(selectedBot.id)} />
      {UpgradeModal}
    </>
  );
}
